import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { formatUserDisplayName } from '@/utils/formatters'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import type { UserSummary } from '@/api/shares'
import { PermissionSelect } from './PermissionSelect'
import { getPermissionLabel } from './permissionLevels'

interface PlanAccessRowProps {
  user: UserSummary
  role: 'Owner' | 'Editor'
  canFreeze?: boolean
  /** Show the permission dropdown (owner or admin managing access) */
  editable?: boolean
  onToggleFreeze?: (canFreeze: boolean) => void
  onRemove?: () => void
  isToggling?: boolean
  isRemoving?: boolean
}

export function PlanAccessRow({
  user,
  role,
  canFreeze = false,
  editable = false,
  onToggleFreeze,
  onRemove,
  isToggling,
  isRemoving,
}: PlanAccessRowProps) {
  const colors = getAvatarColor(user.id)
  const name = formatUserDisplayName(user)
  const isEditor = role === 'Editor'

  return (
    <div className="-mx-2 flex min-h-12 items-center justify-between gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback
            className={cn(colors.bg, colors.text, 'text-xs font-medium')}
          >
            {getInitials(user.display_name, user.email)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{name}</p>
          {user.display_name && user.email && (
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          )}
        </div>
      </div>

      <div className="shrink-0">
        {isEditor && editable && onToggleFreeze ? (
          <PermissionSelect
            canFreeze={canFreeze}
            onChange={onToggleFreeze}
            onRemove={onRemove}
            isLoading={isToggling || isRemoving}
            aria-label={`Change access for ${name}`}
          />
        ) : (
          <span className="px-2.5 text-xs text-muted-foreground">
            {isEditor ? getPermissionLabel(canFreeze) : role}
          </span>
        )}
      </div>
    </div>
  )
}
